import React, { useState } from 'react';
import { Upload, FileText, Tag, Users, FolderOpen } from 'lucide-react';
import Button from '../ui/Button';
import Input from '../ui/Input';

interface NewDocumentFormProps {
  onSubmit: (data: any) => void;
  onCancel: () => void;
  loading?: boolean;
  projects?: any[];
  teams?: any[];
  documentTypes?: any[];
  documentSubtypes?: any[];
}

const NewDocumentForm: React.FC<NewDocumentFormProps> = ({ 
  onSubmit, 
  onCancel, 
  loading = false,
  projects = [],
  teams = [],
  documentTypes = [],
  documentSubtypes = []
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    file: null as File | null,
    project: '',
    team: '',
    tags: '',
    isPublic: false,
    cloudProvider: 'local',
    documentType: '',
    documentSubtype: ''
  });

  const [dragActive, setDragActive] = useState(false);

  // Fonction pour obtenir les sous-types disponibles
  const getAvailableSubtypes = () => {
    if (!formData.documentType) return [];
    
    // Filtrer les sous-types qui correspondent au type sélectionné
    return documentSubtypes.filter((subtype: any) => {
      const typeId = typeof subtype.document_type === 'object' 
        ? subtype.document_type?.id 
        : subtype.document_type;
      return typeId && typeId.toString() === formData.documentType.toString();
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData(prev => ({ ...prev, file, name: file.name }));
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      setFormData(prev => ({ ...prev, file, name: file.name }));
    }
  };

  const handleTypeChange = (typeId: string) => {
    setFormData(prev => ({ 
      ...prev, 
      documentType: typeId, 
      documentSubtype: '' // Réinitialiser le sous-type
    }));
  };


  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.file) {
      onSubmit({
        ...formData,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag)
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Upload de fichier */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Fichier à uploader
        </label>
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            dragActive 
              ? 'border-primary-500 bg-primary-50' 
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            type="file"
            onChange={handleFileChange}
            className="hidden"
            id="file-upload"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif"
          />
          <label htmlFor="file-upload" className="cursor-pointer">
            <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600">
              Glissez-déposez votre fichier ici ou{' '}
              <span className="text-primary-600 hover:text-primary-500">
                cliquez pour sélectionner
              </span>
            </p>
            <p className="text-xs text-gray-500 mt-1">
              PDF, Word, Excel, PowerPoint, Images (max 50MB)
            </p>
          </label>
        </div>
        {formData.file && (
          <div className="mt-2 p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center">
              <FileText className="w-5 h-5 text-gray-500 mr-2" />
              <span className="text-sm text-gray-700">{formData.file.name}</span>
              <span className="text-xs text-gray-500 ml-2">
                ({(formData.file.size / 1024 / 1024).toFixed(2)} MB)
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Nom du document */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Nom du document
        </label>
        <Input
          value={formData.name}
          onChange={(value) => setFormData(prev => ({ ...prev, name: value }))}
          placeholder="Nom du document"
          required
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Description <span className="text-xs text-gray-500 font-normal">(rempli automatiquement par le système)</span>
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Description du document..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed resize-none"
          rows={3}
          disabled
        />
      </div>

      {/* Sélection du domaine, type et sous-type de document */}
      <div className="bg-white/60 border border-gray-200 rounded-xl p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Classification du document</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Type de document <span className="text-red-500">*</span>
            </label>
            <select 
              value={formData.documentType}
              onChange={(e) => handleTypeChange(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Sélectionnez un type...</option>
              {documentTypes.map((type) => {
                const domainLabel =
                  (typeof type.domain === 'object' && type.domain?.name) ||
                  type.domain_name ||
                  type.domain?.code ||
                  '';
                const label = domainLabel ? `${domainLabel} • ${type.name}` : type.name;
                return (
                  <option key={type.id} value={type.id}>
                    {label}
                  </option>
                );
              })}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Sous-type <span className="text-red-500">*</span>
            </label>
            <select 
              value={formData.documentSubtype}
              onChange={(e) => setFormData(prev => ({ ...prev, documentSubtype: e.target.value }))}
              disabled={!formData.documentType}
              className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">Sélectionnez un sous-type...</option>
              {getAvailableSubtypes().map((subtype: any) => (
                <option key={subtype.id} value={subtype.id}>
                  {subtype.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        {formData.documentType && formData.documentSubtype && (
          <div className="mt-4 p-3 bg-primary-50 border border-primary-200 rounded-lg">
            <div className="flex items-center">
              <FileText className="w-4 h-4 text-primary-600 mr-2" />
              <span className="text-sm text-primary-800">
                <strong>Type sélectionné :</strong> {documentTypes.find((t: any) => t.id.toString() === formData.documentType.toString())?.name} → {documentSubtypes.find((s: any) => s.id.toString() === formData.documentSubtype.toString())?.name}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Métadonnées */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <FolderOpen className="w-4 h-4 inline mr-1" />
            Projet (optionnel)
          </label>
          <select
            value={formData.project}
            onChange={(e) => setFormData(prev => ({ ...prev, project: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Sélectionner un projet</option>
            {projects.map((project: any) => (
              <option key={project.id} value={project.id}>{project.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Users className="w-4 h-4 inline mr-1" />
            Équipe
          </label>
          <select
            value={formData.team}
            onChange={(e) => setFormData(prev => ({ ...prev, team: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Sélectionner une équipe</option>
            {teams.map((team: any) => (
              <option key={team.id} value={team.id}>{team.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Tags */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <Tag className="w-4 h-4 inline mr-1" />
          Tags (séparés par des virgules)
        </label>
        <Input
          value={formData.tags}
          onChange={(value) => setFormData(prev => ({ ...prev, tags: value }))}
          placeholder="important, rapport, 2024..."
        />
      </div>

      {/* Options */}
      <div className="space-y-3">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="isPublic"
            checked={formData.isPublic}
            onChange={(e) => setFormData(prev => ({ ...prev, isPublic: e.target.checked }))}
            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
          />
          <label htmlFor="isPublic" className="ml-2 text-sm text-gray-700">
            Rendre ce document public
          </label>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Synchroniser avec
          </label>
          <select
            value={formData.cloudProvider}
            onChange={(e) => setFormData(prev => ({ ...prev, cloudProvider: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="local">Stockage local uniquement</option>
            <option value="google-drive">Google Drive</option>
            <option value="onedrive">OneDrive</option>
            <option value="dropbox">Dropbox</option>
            <option value="sharepoint">SharePoint</option>
          </select>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={loading}
        >
          Annuler
        </Button>
        <Button
          type="submit"
          disabled={!formData.file || loading}
          loading={loading}
        >
          {loading ? 'Upload en cours...' : 'Uploader le document'}
        </Button>
      </div>
    </form>
  );
};

export default NewDocumentForm;
